import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Close as CancelIcon,
  Delete as DeleteIcon,
  AccountTree as WorkflowIcon,
  Business as DepartmentIcon,
  Security as RoleIcon,
  Person as EmployeeIcon,
  AttachFile as FileIcon,
  Flag as FinalIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  InfoOutlined as InfoIcon,
  ArrowBack as ArrowBackIcon,
  LayersClear as ClearIcon,
  DynamicForm as DynamicFormIcon,
  Visibility as ViewIcon,
  Tune as FormConfigIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import {
  getForms,
  getFormDetails,
  createForm,
  updateForm,
  deleteForm,
} from "services/company/forms/formServices";
import DynamicTableField from "../forms/DynamicTableField";
import GridColumnConfigurator, { PRESET_SCHEMAS } from "../forms/GridColumnConfigurator";

import {
  getRequisitionWorkflow,
  saveRequisitionWorkflow,
  getRequisitionWorkflowApproverOptions,
} from "services/company/requisition/requisitionServices";

import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import { fetchRolePermissionsData } from "store/features/company/roleManagement/rolePermissionActions";
import { selectRolePermissionRoles } from "store/features/company/roleManagement/rolePermissionSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";

import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const APPROVER_TYPES = [
  { value: "department", label: "Department", icon: <DepartmentIcon fontSize="small" /> },
  { value: "role", label: "Role", icon: <RoleIcon fontSize="small" /> },
  { value: "employee", label: "Specific Employee", icon: <EmployeeIcon fontSize="small" /> },
];

const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "number", label: "Number" },
  { value: "textarea", label: "Long Text / Notes" },
  { value: "boolean", label: "Yes / No (Boolean)" },
  { value: "select", label: "Dropdown Select" },
  { value: "table", label: "Table / Line Items Grid" },
  { value: "date", label: "Date" },
];

// Helper to safely extract array of step records from any Axios response variation
const extractStepsArray = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.response)) return res.response;
  return [];
};

// Helper to safely extract approver options object
const extractOptionsObject = (res) => {
  const root = res?.data?.data || res?.data || res || {};
  return {
    departments: Array.isArray(root.departments) ? root.departments : [],
    roles: Array.isArray(root.roles) ? root.roles : [],
    employees: Array.isArray(root.employees) ? root.employees : [],
  };
};

export default function RequisitionWorkflows() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux Selectors (matches AddEmployeeModal and Create User modal pattern)
  const reduxDepartments = useSelector(selectDepartments);
  const reduxRoles = useSelector(selectRolePermissionRoles);
  const reduxEmployees = useSelector(selectEmployees);

  // Fallback API options
  const [approverOptions, setApproverOptions] = useState({
    departments: [],
    roles: [],
    employees: [],
  });

  // Main state
  const [savedSteps, setSavedSteps] = useState([]);
  const [editingSteps, setEditingSteps] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const [availableForms, setAvailableForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("new");
  const [formLoading, setFormLoading] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formDeleting, setFormDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState(0); // 0 = Builder/Editor, 1 = Live Preview Output
  const [previewValues, setPreviewValues] = useState({});

  const [formEditorState, setFormEditorState] = useState({
    id: null,
    form_name: "",
    description: "",
    fields: [
      {
        field_name: "budget_available",
        field_label: "Budget Available",
        field_type: "boolean",
        required: true,
        options: "",
      },
      {
        field_name: "approved_amount",
        field_label: "Approved Amount",
        field_type: "number",
        required: true,
        options: "",
      },
      {
        field_name: "remarks",
        field_label: "Approval Remarks",
        field_type: "textarea",
        required: false,
        options: "",
      },
    ],
  });

  const fetchFormsList = async (autoSelectFirst = false) => {
    try {
      const res = await getForms();
      const list = res?.data?.data || res?.data || res || [];
      const safeList = Array.isArray(list) ? list : [];
      setAvailableForms(safeList);
      if (autoSelectFirst && safeList.length > 0) {
        handleSelectFormForEditing(safeList[0].id);
      }
    } catch (e) {
      console.error("Failed to load forms list:", e);
    }
  };

  const handleSelectFormForEditing = async (formId) => {
    if (!formId || formId === "new") {
      setSelectedFormId("new");
      setFormEditorState({
        id: null,
        form_name: "",
        description: "",
        fields: [
          {
            field_name: "budget_available",
            field_label: "Budget Available",
            field_type: "boolean",
            required: true,
            options: "",
          },
          {
            field_name: "approved_amount",
            field_label: "Approved Amount",
            field_type: "number",
            required: true,
            options: "",
          },
          {
            field_name: "remarks",
            field_label: "Approval Remarks",
            field_type: "textarea",
            required: false,
            options: "",
          },
        ],
      });
      setPreviewValues({});
      return;
    }

    try {
      setSelectedFormId(formId);
      setFormLoading(true);
      const res = await getFormDetails(formId);
      const data = res?.data?.data || res?.data || res;
      if (data) {
        setFormEditorState({
          id: data.id,
          form_name: data.form_name || "",
          description: data.description || "",
          fields:
            Array.isArray(data.fields) && data.fields.length > 0
              ? data.fields.map((f, i) => ({
                  id: f.id,
                  field_name: f.field_name || `field_${i + 1}`,
                  field_label: f.field_label || "",
                  field_type: f.field_type || "text",
                  required: Boolean(f.required),
                  options:
                    f.field_type === "table"
                      ? Array.isArray(f.options) && f.options.length > 0
                        ? f.options
                        : typeof f.options === "string"
                        ? (() => {
                            try {
                              const p = JSON.parse(f.options);
                              return Array.isArray(p) ? p : PRESET_SCHEMAS.material_service.columns;
                            } catch (e) {
                              return PRESET_SCHEMAS.material_service.columns;
                            }
                          })()
                        : PRESET_SCHEMAS.material_service.columns
                      : Array.isArray(f.options)
                      ? f.options.join(", ")
                      : typeof f.options === "string"
                      ? f.options
                      : "",
                }))
              : [
                  {
                    field_name: "field_1",
                    field_label: "Field 1",
                    field_type: "text",
                    required: false,
                    options: "",
                  },
                ],
        });

        const initPreview = {};
        if (data.fields) {
          data.fields.forEach((f) => {
            initPreview[f.id || f.field_name] =
              f.field_type === "boolean" ? false : "";
          });
        }
        setPreviewValues(initPreview);
      }
    } catch (err) {
      console.error("Failed to load form details:", err);
      showErrorToast("Failed to load form details");
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveForm = async () => {
    if (!formEditorState.form_name.trim()) {
      showErrorToast("Form name is required");
      return;
    }

    if (formEditorState.fields.length === 0) {
      showErrorToast("Please add at least one field to this form");
      return;
    }

    for (let i = 0; i < formEditorState.fields.length; i++) {
      if (!formEditorState.fields[i].field_label.trim()) {
        showErrorToast(`Field #${i + 1} must have a label`);
        return;
      }
    }

    try {
      setFormSaving(true);
      const formattedFields = formEditorState.fields.map((f, i) => {
        let optionsArr = null;
        if (f.field_type === "select" && f.options) {
          optionsArr =
            typeof f.options === "string"
              ? f.options.split(",").map((s) => s.trim()).filter(Boolean)
              : Array.isArray(f.options)
              ? f.options
              : null;
        } else if (f.field_type === "table") {
          optionsArr =
            Array.isArray(f.options) && f.options.length > 0
              ? f.options
              : PRESET_SCHEMAS.material_service.columns;
        }
        return {
          field_name:
            f.field_name ||
            f.field_label.toLowerCase().replace(/[^a-z0-9_]/g, "_") ||
            `field_${i + 1}`,
          field_label: f.field_label.trim(),
          field_type: f.field_type,
          required: f.required ? 1 : 0,
          options: optionsArr,
          field_order: i + 1,
        };
      });

      const payload = {
        form_name: formEditorState.form_name.trim(),
        description: formEditorState.description.trim() || null,
        fields: formattedFields,
      };

      let savedId = formEditorState.id;
      if (formEditorState.id) {
        await updateForm(formEditorState.id, payload);
        showSuccessToast("Form updated successfully!");
      } else {
        const res = await createForm(payload);
        const data = res?.data?.data || res?.data || res;
        savedId = data?.id;
        showSuccessToast("Form created successfully!");
      }

      await fetchFormsList(false);
      if (savedId) {
        await handleSelectFormForEditing(savedId);
        // If an editing step or saved step had this form, update form_name
        setEditingSteps((prev) =>
          prev.map((s) =>
            String(s.formId) === String(savedId)
              ? { ...s, form_name: formEditorState.form_name.trim() }
              : s
          )
        );
        setSavedSteps((prev) =>
          prev.map((s) =>
            String(s.formId) === String(savedId)
              ? { ...s, form_name: formEditorState.form_name.trim() }
              : s
          )
        );
      }
    } catch (err) {
      showErrorToast(err?.message || "Failed to save form");
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteForm = async () => {
    if (!formEditorState.id) return;
    try {
      setFormDeleting(true);
      await deleteForm(formEditorState.id);
      showSuccessToast(`Form "${formEditorState.form_name}" deleted.`);
      setDeleteConfirmOpen(false);

      // Unlink deleted form from any steps
      setEditingSteps((prev) =>
        prev.map((s) =>
          String(s.formId) === String(formEditorState.id)
            ? { ...s, formId: "", form_name: "" }
            : s
        )
      );
      setSavedSteps((prev) =>
        prev.map((s) =>
          String(s.formId) === String(formEditorState.id)
            ? { ...s, formId: "", form_name: "" }
            : s
        )
      );

      const res = await getForms();
      const list = res?.data?.data || res?.data || res || [];
      const safeList = Array.isArray(list) ? list : [];
      setAvailableForms(safeList);
      if (safeList.length > 0) {
        handleSelectFormForEditing(safeList[0].id);
      } else {
        handleSelectFormForEditing("new");
      }
    } catch (err) {
      showErrorToast(err?.message || "Failed to delete form");
    } finally {
      setFormDeleting(false);
    }
  };

  // Fetch initial workflow and options on mount
  useEffect(() => {
    fetchWorkflowLayout();
    fetchFormsList(true);
    dispatch(fetchDepartments());
    dispatch(fetchRolePermissionsData());
    dispatch(fetchEmployees());
    fetchFallbackOptions();
  }, [dispatch]);

  // Load the company's single saved workflow
  const fetchWorkflowLayout = async () => {
    try {
      setLoading(true);
      const res = await getRequisitionWorkflow();
      const rawData = extractStepsArray(res);

      const formatted = rawData.map((s, idx) => ({
        id: s.id,
        step_order: s.step_order || idx + 1,
        approver_type: s.approver_type || "department",
        approver_id: s.approver_id != null ? String(s.approver_id) : "",
        approver_name: s.approver_name || "",
        is_file_required: s.is_file_required ? 1 : 0,
        file_url: s.file_url || "",
        isFinal: s.isFinal ? 1 : 0,
        formId: s.formId || "",
        form_name: s.form_name || "",
      }));

      setSavedSteps(formatted);
      setEditingSteps(formatted);
    } catch (err) {
      const msg = err?.meta?.message || err?.message || "Failed to load workflow layout";
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchFallbackOptions = async () => {
    try {
      const res = await getRequisitionWorkflowApproverOptions();
      const options = extractOptionsObject(res);
      setApproverOptions(options);
    } catch (err) {
      console.error("Failed to load fallback approver options:", err);
    }
  };

  // Switch to Edit Mode
  const handleStartEdit = () => {
    dispatch(fetchDepartments());
    dispatch(fetchRolePermissionsData());
    dispatch(fetchEmployees());
    fetchFallbackOptions();

    if (savedSteps.length === 0) {
      // Default to 1 step if company has no steps yet
      setEditingSteps([
        {
          step_order: 1,
          approver_type: "department",
          approver_id: "",
          approver_name: "",
          is_file_required: 0,
          file_url: "",
          isFinal: 1,
          formId: "",
        },
      ]);
    } else {
      setEditingSteps(savedSteps.map((s) => ({ ...s })));
    }
    setIsEditing(true);
  };

  // Cancel Editing & revert changes
  const handleCancelEdit = () => {
    setEditingSteps(savedSteps.map((s) => ({ ...s })));
    setIsEditing(false);
  };

  // Add a new step in Edit Mode
  const handleAddStep = () => {
    setEditingSteps((prev) => {
      const nextOrder = prev.length + 1;
      const updated = prev.map((s) => ({ ...s, isFinal: 0 }));
      updated.push({
        step_order: nextOrder,
        approver_type: "department",
        approver_id: "",
        approver_name: "",
        is_file_required: 0,
        file_url: "",
        isFinal: 1,
        formId: "",
      });
      return updated;
    });
  };

  // Remove a step in Edit Mode
  const handleRemoveStep = (index) => {
    setEditingSteps((prev) => {
      if (prev.length <= 1) {
        showErrorToast("At least 1 step is required in edit mode. Use 'Clear Workflow' to remove all steps.");
        return prev;
      }
      const filtered = prev.filter((_, i) => i !== index);
      const hasFinal = filtered.some((s) => s.isFinal === 1);
      return filtered.map((s, idx) => ({
        ...s,
        step_order: idx + 1,
        isFinal: !hasFinal && idx === filtered.length - 1 ? 1 : s.isFinal,
      }));
    });
  };

  // Move step up or down
  const handleMoveStep = (index, direction) => {
    setEditingSteps((prev) => {
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const reordered = [...prev];
      const temp = reordered[index];
      reordered[index] = reordered[targetIndex];
      reordered[targetIndex] = temp;

      return reordered.map((s, idx) => ({
        ...s,
        step_order: idx + 1,
      }));
    });
  };

  // Update step field in Edit Mode
  const handleStepChange = (index, field, value) => {
    setEditingSteps((prev) => {
      const updated = [...prev];

      if (field === "isFinal") {
        const val = value ? 1 : 0;
        // If marking this step final, remove final from other steps
        return updated.map((s, idx) => ({
          ...s,
          isFinal: idx === index ? val : (val === 1 ? 0 : s.isFinal),
        }));
      }

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      if (field === "approver_type") {
        updated[index].approver_id = "";
        updated[index].approver_name = "";
      }

      return updated;
    });
  };

  // Save workflow layout to database
  const handleSaveWorkflow = async () => {
    if (editingSteps.length === 0) {
      showErrorToast("Please add at least 1 approval step or cancel editing.");
      return;
    }

    for (let i = 0; i < editingSteps.length; i++) {
      const step = editingSteps[i];
      if (!step.approver_id) {
        showErrorToast(`Step ${i + 1}: Please select an approver target`);
        return;
      }
    }

    // Ensure at least one step is marked isFinal
    const hasFinal = editingSteps.some((s) => s.isFinal === 1);
    const preparedSteps = editingSteps.map((s, idx) => ({
      step_order: idx + 1,
      approver_type: s.approver_type,
      approver_id: Number(s.approver_id),
      is_file_required: s.is_file_required ? 1 : 0,
      file_url: s.file_url ? s.file_url.trim() : null,
      isFinal: !hasFinal && idx === editingSteps.length - 1 ? 1 : (s.isFinal ? 1 : 0),
      formId: s.formId ? Number(s.formId) : null,
    }));

    try {
      setSaving(true);
      const res = await saveRequisitionWorkflow(preparedSteps);
      showSuccessToast("Approval workflow saved successfully!");

      const rawData = extractStepsArray(res);
      if (rawData.length > 0) {
        const formatted = rawData.map((s, idx) => ({
          id: s.id,
          step_order: s.step_order || idx + 1,
          approver_type: s.approver_type,
          approver_id: s.approver_id != null ? String(s.approver_id) : "",
          approver_name: s.approver_name || "",
          is_file_required: s.is_file_required ? 1 : 0,
          file_url: s.file_url || "",
          isFinal: s.isFinal ? 1 : 0,
          formId: s.formId || "",
        }));
        setSavedSteps(formatted);
        setEditingSteps(formatted);
      } else {
        await fetchWorkflowLayout();
      }

      setIsEditing(false); // Return to View Mode
    } catch (err) {
      const msg = err?.meta?.message || err?.message || "Failed to save workflow layout";
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  // Clear workflow entirely (revert to direct approval)
  const handleConfirmClear = async () => {
    try {
      setSaving(true);
      await saveRequisitionWorkflow([]);
      showSuccessToast("Workflow cleared. Requisitions will now follow direct approval.");
      setSavedSteps([]);
      setEditingSteps([]);
      setIsEditing(false);
      setClearDialogOpen(false);
    } catch (err) {
      const msg = err?.meta?.message || err?.message || "Failed to clear workflow";
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  // Resolve target options for each approver type
  const getTargetOptions = (approverType) => {
    if (approverType === "department") {
      const list =
        reduxDepartments && reduxDepartments.length > 0
          ? reduxDepartments
          : approverOptions.departments || [];
      return list.map((d) => ({
        id: String(d.id),
        name: d.name,
        code: d.code || "",
      }));
    }
    if (approverType === "role") {
      const list =
        reduxRoles && reduxRoles.length > 0
          ? reduxRoles
          : approverOptions.roles || [];
      return list.map((r) => ({
        id: String(r.id),
        name: r.name,
        code: r.code || "",
      }));
    }
    const list =
      reduxEmployees && reduxEmployees.length > 0
        ? reduxEmployees
        : approverOptions.employees || [];
    return list.map((emp) => ({
      id: String(emp.id),
      name: `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.name || "",
      employee_code: emp.employee_code || "",
      email: emp.email || "",
    }));
  };

  // Helper to get formatted display name for View Mode
  const getApproverDisplayName = (step) => {
    const options = getTargetOptions(step.approver_type);
    const found = options.find((opt) => String(opt.id) === String(step.approver_id));
    if (found) {
      if (step.approver_type === "department") {
        return `${found.name}${found.code ? ` (${found.code})` : ""}`;
      }
      if (step.approver_type === "employee") {
        return `${found.name}${found.employee_code ? ` (${found.employee_code})` : ""}`;
      }
      return found.name;
    }
    if (step.approver_name) return step.approver_name;
    return `ID #${step.approver_id}`;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", maxWidth: 1720, mx: "auto" }}>
      {/* ================= HEADER ================= */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.25 },
          mb: 2,
          borderRadius: 1,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          bgcolor: isDark ? "rgba(30, 41, 59, 0.5)" : "#fff",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Tooltip title="Back to Requisitions">
              <IconButton
                onClick={() => navigate("/company/requisitions")}
                size="small"
                sx={{
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                  borderRadius: 1,
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                p: 1.25,
                borderRadius: 1,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(79, 70, 229, 0.1)",
                color: "#4f46e5",
                display: "flex",
                alignItems: "center",
              }}
            >
              <WorkflowIcon sx={{ fontSize: 28 }} />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: "-0.5px" }}>
                  Approval Workflow
                </Typography>
                <Chip
                  label={
                    isEditing
                      ? "Edit Mode"
                      : savedSteps.length > 0
                      ? `${savedSteps.length} Step${savedSteps.length === 1 ? "" : "s"}`
                      : "Not Configured"
                  }
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: isEditing
                      ? "rgba(245, 158, 11, 0.12)"
                      : savedSteps.length > 0
                      ? "rgba(79, 70, 229, 0.1)"
                      : "rgba(100, 116, 139, 0.1)",
                    color: isEditing
                      ? "#f59e0b"
                      : savedSteps.length > 0
                      ? "#4f46e5"
                      : "text.secondary",
                    borderRadius: "6px",
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isEditing
                  ? "Configure the sequential approval steps for your organization."
                  : "View the active single approval sequence applied to all company requisitions."}
              </Typography>
            </Box>
          </Stack>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {!isEditing ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchWorkflowLayout}
                  startIcon={<RefreshIcon />}
                  disabled={loading}
                  sx={{ textTransform: "none", borderRadius: 1 }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleStartEdit}
                  disabled={loading}
                  startIcon={<EditIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1,
                    bgcolor: "#4f46e5",
                    "&:hover": { bgcolor: "#4338ca" },
                    boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                    px: 2.5,
                    fontWeight: 600,
                  }}
                >
                  {savedSteps.length > 0 ? "Edit Workflow" : "Configure Workflow"}
                </Button>
              </>
            ) : (
              <>
                {savedSteps.length > 0 && (
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={() => setClearDialogOpen(true)}
                    disabled={saving}
                    startIcon={<ClearIcon />}
                    sx={{ textTransform: "none", mr: 1 }}
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  startIcon={<CancelIcon />}
                  sx={{ textTransform: "none", borderRadius: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleSaveWorkflow}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1,
                    bgcolor: "#4f46e5",
                    "&:hover": { bgcolor: "#4338ca" },
                    boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                    px: 2.5,
                    fontWeight: 600,
                  }}
                >
                  {saving ? "Saving..." : "Save Workflow"}
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ================= MAIN CONTENT (STRICTLY SIDE BY SIDE) ================= */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2.5,
          width: "100%",
          alignItems: "flex-start",
          minWidth: 0,
          overflowX: "auto",
          pb: 1,
        }}
      >
        {/* ================= LEFT COLUMN: WORKFLOW STEPS ================= */}
        <Box
          sx={{
            flex: "1 1 50%",
            width: "50%",
            minWidth: 400,
            maxWidth: "50%",
          }}
        >
          {loading ? (
        <Paper
          elevation={0}
          sx={{
            p: 8,
            borderRadius: 1,
            textAlign: "center",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          }}
        >
          <CircularProgress size={36} sx={{ color: "#4f46e5" }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading company approval workflow...
          </Typography>
        </Paper>
      ) : !isEditing ? (
        /* ================= VIEW MODE ================= */
        savedSteps.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 1,
              textAlign: "center",
              border: `1px dashed ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
              bgcolor: isDark ? "rgba(30, 41, 59, 0.3)" : "#fafafa",
            }}
          >
            <WorkflowIcon sx={{ fontSize: 56, color: "text.disabled", mb: 1.5 }} />
            <Typography variant="h6" fontWeight={600} color="text.primary">
              No Approval Workflow Configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 460, mx: "auto" }}>
              Currently, requisitions submitted in your company use direct approval. Configure the steps below to enable multi-step department, role, or employee approvals.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleStartEdit}
              sx={{
                borderRadius: 1,
                bgcolor: "#4f46e5",
                "&:hover": { bgcolor: "#4338ca" },
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              Configure Approval Workflow
            </Button>
          </Paper>
        ) : (
          <Stack spacing={1.25}>
            {/* Informational Callout */}
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.08)" : "rgba(79, 70, 229, 0.05)",
                border: `1px solid ${isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(79, 70, 229, 0.15)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <InfoIcon fontSize="small" sx={{ color: "#4f46e5" }} />
                <Typography variant="body2" color="text.secondary">
                  Active company pipeline with <strong>{savedSteps.length} step{savedSteps.length === 1 ? "" : "s"}</strong>.
                  Submitted requisitions will follow this sequence automatically.
                </Typography>
              </Stack>

              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon fontSize="small" />}
                onClick={handleStartEdit}
                sx={{
                  borderRadius: 1,
                  borderColor: "#4f46e5",
                  color: "#4f46e5",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Edit Steps
              </Button>
            </Box>

            {/* Stepper Pipeline */}
            {savedSteps.map((step, index) => {
              const isDept = step.approver_type === "department";
              const isRole = step.approver_type === "role";
              const displayName = getApproverDisplayName(step);

              return (
                <React.Fragment key={step.id || index}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 1,
                      border: `1px solid ${
                        step.isFinal === 1
                          ? "#10b981"
                          : isDark
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.08)"
                      }`,
                      bgcolor: isDark ? "rgba(30, 41, 59, 0.6)" : "#fff",
                      boxShadow:
                        step.isFinal === 1
                          ? "0 4px 14px rgba(16, 185, 129, 0.15)"
                          : "0 2px 8px rgba(0,0,0,0.03)",
                      transition: "transform 0.15s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <CardContent sx={{ px: 1.5, py: 1.25, "&:last-child": { pb: 1.25 } }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1.25}
                      >
                        {/* Left: Step Info */}
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Chip
                            label={`Step ${step.step_order}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: "#4f46e5",
                              color: "#fff",
                              borderRadius: 1,
                              height: 26,
                              fontSize: "0.75rem",
                            }}
                          />

                          <Box
                            sx={{
                              p: 0.9,
                              borderRadius: 1,
                              bgcolor: isDept
                                ? "rgba(59, 130, 246, 0.12)"
                                : isRole
                                ? "rgba(139, 92, 246, 0.12)"
                                : "rgba(245, 158, 11, 0.12)",
                              color: isDept ? "#3b82f6" : isRole ? "#8b5cf6" : "#f59e0b",
                              display: "flex",
                            }}
                          >
                            {isDept ? (
                              <DepartmentIcon fontSize="small" />
                            ) : isRole ? (
                              <RoleIcon fontSize="small" />
                            ) : (
                              <EmployeeIcon fontSize="small" />
                            )}
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px", lineHeight: 1.2 }}
                            >
                              {step.approver_type} Approval
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                              {displayName}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Right: Badges */}
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          {step.formId && (
                            <Chip
                              icon={<DynamicFormIcon style={{ fontSize: 14, color: "#6366f1" }} />}
                              label={step.form_name ? `Form: ${step.form_name}` : `Form #${step.formId}`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "#eef2ff",
                                color: "#4f46e5",
                                borderRadius: 1,
                              }}
                            />
                          )}

                          {step.is_file_required === 1 && (
                            <Chip
                              icon={<FileIcon style={{ fontSize: 14, color: "#0284c7" }} />}
                              label="S3 File Upload Mandatory"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                bgcolor: "rgba(2, 132, 199, 0.1)",
                                color: "#0284c7",
                                borderRadius: 1,
                              }}
                            />
                          )}

                          {step.isFinal === 1 ? (
                            <Chip
                              icon={<FinalIcon style={{ fontSize: 14, color: "#10b981" }} />}
                              label="Final Approval Step (Completes Status)"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(16, 185, 129, 0.12)",
                                color: "#10b981",
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            <Chip
                              label="Intermediate Step"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                                color: "text.secondary",
                                borderRadius: 1,
                              }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Flow Arrow between steps */}
                  {index < savedSteps.length - 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 0 }}>
                      <ArrowForwardIcon
                        sx={{
                          color: "text.disabled",
                          transform: "rotate(90deg)",
                          fontSize: 18,
                        }}
                      />
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Stack>
        )
      ) : (
        /* ================= EDIT MODE ================= */
        <Stack spacing={1.5}>
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 1,
              bgcolor: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.2)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <InfoIcon fontSize="small" sx={{ color: "#f59e0b" }} />
            <Typography variant="body2" color="text.secondary">
              Edit the approval order, approver types, and conditions below. Use the arrows to reorder steps. Mark exactly one step as <strong>Final Approval Step</strong>.
            </Typography>
          </Box>

          {editingSteps.map((step, index) => {
            const targetOptions = getTargetOptions(step.approver_type);
            const isDept = step.approver_type === "department";
            const isRole = step.approver_type === "role";

            // Determine if current step's approver_id matches any available option
            const hasMatch = targetOptions.some((o) => o.id === String(step.approver_id));

            return (
              <Card
                key={index}
                elevation={0}
                sx={{
                  borderRadius: 1,
                  border: `1px solid ${
                    step.isFinal === 1
                      ? "#10b981"
                      : isDark
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(0,0,0,0.1)"
                  }`,
                  bgcolor: isDark ? "rgba(30, 41, 59, 0.6)" : "#fff",
                  boxShadow: step.isFinal === 1 ? "0 0 14px rgba(16, 185, 129, 0.15)" : undefined,
                }}
              >
                <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                  {/* Step Header */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.5}
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip
                        label={`Step ${index + 1}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: "#4f46e5",
                          color: "#fff",
                          borderRadius: 1,
                        }}
                      />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {isDept
                          ? "Department Approval"
                          : isRole
                          ? "Role-Based Approval"
                          : "Specific Employee Approval"}
                      </Typography>
                    </Stack>

                    {/* Step Actions */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Move Step Up">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleMoveStep(index, "up")}
                          >
                            <MoveUpIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move Step Down">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === editingSteps.length - 1}
                            onClick={() => handleMoveStep(index, "down")}
                          >
                            <MoveDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove Step">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveStep(index)}
                          sx={{ color: "#ef4444" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {/* Form Controls */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                    {/* Approver Type */}
                    <FormControl size="small" sx={{ minWidth: { sm: 190 } }}>
                      <InputLabel>Approver Type</InputLabel>
                      <Select
                        value={step.approver_type}
                        label="Approver Type"
                        onChange={(e) => handleStepChange(index, "approver_type", e.target.value)}
                      >
                        {APPROVER_TYPES.map((at) => (
                          <MenuItem key={at.value} value={at.value}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {at.icon}
                              <span>{at.label}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Approver Target Selection */}
                    <FormControl fullWidth size="small" required>
                      <InputLabel>
                        Select{" "}
                        {step.approver_type === "department"
                          ? "Department"
                          : step.approver_type === "role"
                          ? "Role"
                          : "Employee"}
                      </InputLabel>
                      <Select
                        value={step.approver_id !== "" && step.approver_id != null ? String(step.approver_id) : ""}
                        label={`Select ${
                          step.approver_type === "department"
                            ? "Department"
                            : step.approver_type === "role"
                            ? "Role"
                            : "Employee"
                        }`}
                        onChange={(e) => handleStepChange(index, "approver_id", e.target.value)}
                      >
                        <MenuItem value="" disabled>
                          <em>Select {step.approver_type}</em>
                        </MenuItem>

                        {/* Preserved selected option fallback if loading */}
                        {step.approver_id && !hasMatch && (
                          <MenuItem value={String(step.approver_id)}>
                            {step.approver_name || `Selected (${step.approver_id})`}
                          </MenuItem>
                        )}

                        {targetOptions.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {step.approver_type === "department"
                              ? `${item.name}${item.code ? ` (${item.code})` : ""}`
                              : step.approver_type === "employee"
                              ? `${item.name}${item.employee_code ? ` (${item.employee_code})` : item.email ? ` (${item.email})` : ""}`
                              : item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Dynamic Form Selector */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { xs: "100%", sm: 260 } }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Dynamic Form (Optional)</InputLabel>
                        <Select
                          value={step.formId ? String(step.formId) : ""}
                          label="Dynamic Form (Optional)"
                          onChange={(e) => {
                            const val = e.target.value;
                            const matched = availableForms.find((f) => String(f.id) === String(val));
                            handleStepChange(index, "formId", val ? Number(val) : "");
                            handleStepChange(index, "form_name", matched ? matched.form_name : "");
                            if (val) {
                              handleSelectFormForEditing(Number(val));
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>None (No Form)</em>
                          </MenuItem>
                          {availableForms.map((f) => (
                            <MenuItem key={f.id} value={String(f.id)}>
                              {f.form_name} {f.field_count != null ? `(${f.field_count} fields)` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip title="Create a new dynamic form on the right panel" arrow>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSelectFormForEditing("new")}
                          sx={{
                            minWidth: "auto",
                            px: 1.5,
                            py: 0.8,
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          + New
                        </Button>
                      </Tooltip>
                      {step.formId && (
                        <Tooltip title="View & edit this form on right panel" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSelectFormForEditing(step.formId)}
                            sx={{ border: "1px solid rgba(99, 102, 241, 0.3)", borderRadius: "8px" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>

                  {/* Toggles */}
                  <Divider sx={{ my: 1.5, opacity: 0.6 }} />

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={3}
                    alignItems={{ sm: "center" }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={step.is_file_required === 1}
                          onChange={(e) =>
                            handleStepChange(index, "is_file_required", e.target.checked ? 1 : 0)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <FileIcon fontSize="small" sx={{ color: "#0284c7" }} />
                          <Typography variant="body2" color="text.secondary">
                            Mandatory S3 File Upload on Approval
                          </Typography>
                        </Stack>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={step.isFinal === 1}
                          onChange={(e) =>
                            handleStepChange(index, "isFinal", e.target.checked ? 1 : 0)
                          }
                          color="success"
                        />
                      }
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <FinalIcon fontSize="small" sx={{ color: "#10b981" }} />
                          <Typography variant="body2" fontWeight={step.isFinal === 1 ? 700 : 400}>
                            Final Approval Step (Completes Requisition Status)
                          </Typography>
                        </Stack>
                      }
                    />
                  </Stack>
                </CardContent>
              </Card>
            );
          })}

          {/* Action Row */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{ pt: 1 }}
          >
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddStep}
              sx={{
                borderRadius: 1,
                borderColor: "#4f46e5",
                color: "#4f46e5",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Add Another Step
            </Button>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={saving}
                sx={{ borderRadius: 1, textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveWorkflow}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                sx={{
                  borderRadius: 1,
                  bgcolor: "#4f46e5",
                  "&:hover": { bgcolor: "#4338ca" },
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {saving ? "Saving..." : "Save Workflow"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
        </Box>

        {/* ================= RIGHT COLUMN: DYNAMIC FORM DESIGNER & OUTPUT VIEW ================= */}
        <Box
          sx={{
            flex: "1 1 50%",
            width: "50%",
            minWidth: 400,
            maxWidth: "50%",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 1.5,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              bgcolor: isDark ? "rgba(30, 41, 59, 0.5)" : "#fff",
              height: "fit-content",
            }}
          >
            {/* Right Panel Header */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    p: 1.1,
                    borderRadius: 1.5,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "#eef2ff",
                    color: "#4f46e5",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <DynamicFormIcon fontSize="medium" />
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" fontWeight={800}>
                      Form Designer & Output
                    </Typography>
                    <Chip
                      size="small"
                      label={`${availableForms.length} Form${availableForms.length === 1 ? "" : "s"}`}
                      sx={{
                        fontWeight: 700,
                        bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "#e0e7ff",
                        color: "#4338ca",
                        height: 22,
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Create & edit confidential forms attached to workflow steps
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleSelectFormForEditing("new")}
                  sx={{
                    bgcolor: "#4f46e5",
                    "&:hover": { bgcolor: "#4338ca" },
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1,
                  }}
                >
                  + New Form
                </Button>
                {formEditorState.id && (
                  <Tooltip title="Delete this dynamic form">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfirmOpen(true)}
                      sx={{ border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {/* Form Selector Dropdown */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Select Form to Edit / Preview</InputLabel>
                <Select
                  value={selectedFormId}
                  label="Select Form to Edit / Preview"
                  onChange={(e) => handleSelectFormForEditing(e.target.value)}
                  sx={{
                    borderRadius: 1,
                    bgcolor: isDark ? "rgba(15, 23, 42, 0.6)" : "#f8fafc",
                    fontWeight: 600,
                  }}
                >
                  <MenuItem value="new" sx={{ fontWeight: 700, color: "#4f46e5" }}>
                    <em>+ Create New Form...</em>
                  </MenuItem>
                  <Divider />
                  {availableForms.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                        <Typography variant="body2" fontWeight={600}>
                          #{f.id} — {f.form_name}
                        </Typography>
                        <Chip
                          label={`${f.field_count != null ? f.field_count : 0} fields`}
                          size="small"
                          sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Tabs: Form Editor vs Live Output */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={rightPanelTab}
                onChange={(_, v) => setRightPanelTab(v)}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight: 38,
                  "& .MuiTab-root": {
                    minHeight: 38,
                    py: 0.5,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                  },
                }}
              >
                <Tab icon={<EditIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Form Builder & Fields" />
                <Tab icon={<PreviewIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Live Approver Output" />
              </Tabs>
            </Box>

            {/* Tab Body */}
            {formLoading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={32} sx={{ color: "#4f46e5" }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Loading form details...
                </Typography>
              </Box>
            ) : rightPanelTab === 0 ? (
              /* TAB 0: FORM BUILDER & FIELD EDITOR */
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Form Name *"
                  placeholder="e.g. Budget Approval Form, Checklist, IT Review"
                  value={formEditorState.form_name}
                  onChange={(e) =>
                    setFormEditorState((prev) => ({ ...prev, form_name: e.target.value }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      bgcolor: isDark ? "rgba(15, 23, 42, 0.6)" : "#fff",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Description (Optional)"
                  placeholder="Explain what approvers should know or fill out..."
                  multiline
                  rows={2}
                  value={formEditorState.description}
                  onChange={(e) =>
                    setFormEditorState((prev) => ({ ...prev, description: e.target.value }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      bgcolor: isDark ? "rgba(15, 23, 42, 0.6)" : "#fff",
                    },
                  }}
                />

                <Divider sx={{ my: 0.5 }} />

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Form Input Fields ({formEditorState.fields.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setFormEditorState((prev) => ({
                        ...prev,
                        fields: [
                          ...prev.fields,
                          {
                            field_name: `field_${prev.fields.length + 1}`,
                            field_label: `Field ${prev.fields.length + 1}`,
                            field_type: "text",
                            required: false,
                            options: "",
                          },
                        ],
                      }));
                    }}
                    sx={{ textTransform: "none", borderRadius: 1, fontWeight: 700 }}
                  >
                    Add Field
                  </Button>
                </Stack>

                <Stack spacing={1.5}>
                  {formEditorState.fields.map((field, idx) => (
                    <Card
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                        bgcolor: isDark ? "rgba(15, 23, 42, 0.5)" : "#f8fafc",
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                          <Chip
                            label={`#${idx + 1}`}
                            size="small"
                            sx={{ fontWeight: 800, alignSelf: "flex-start", bgcolor: "#4f46e5", color: "#fff", height: 24 }}
                          />
                          <TextField
                            size="small"
                            label="Field Label *"
                            value={field.field_label}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormEditorState((prev) => {
                                const copy = [...prev.fields];
                                copy[idx] = {
                                  ...copy[idx],
                                  field_label: val,
                                  field_name: val.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                                };
                                return { ...prev, fields: copy };
                              });
                            }}
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": { bgcolor: isDark ? "#1e293b" : "#fff", borderRadius: 1 },
                            }}
                          />
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                              value={field.field_type}
                              label="Type"
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormEditorState((prev) => {
                                  const copy = [...prev.fields];
                                  copy[idx] = { ...copy[idx], field_type: val };
                                  return { ...prev, fields: copy };
                                });
                              }}
                              sx={{ bgcolor: isDark ? "#1e293b" : "#fff", borderRadius: 1 }}
                            >
                              {FIELD_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                  {t.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Tooltip title="Delete Field">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={formEditorState.fields.length <= 1}
                                onClick={() => {
                                  setFormEditorState((prev) => ({
                                    ...prev,
                                    fields: prev.fields.filter((_, i) => i !== idx),
                                  }));
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>

                        {field.field_type === "select" && (
                          <TextField
                            size="small"
                            fullWidth
                            label="Dropdown Options (comma separated) *"
                            placeholder="e.g. Yes, No, Needs Review"
                            value={field.options}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormEditorState((prev) => {
                                const copy = [...prev.fields];
                                copy[idx] = { ...copy[idx], options: val };
                                return { ...prev, fields: copy };
                              });
                            }}
                            helperText="Separate options with commas"
                            sx={{ "& .MuiOutlinedInput-root": { bgcolor: isDark ? "#1e293b" : "#fff", borderRadius: 1 } }}
                          />
                        )}

                        {field.field_type === "table" && (
                          <GridColumnConfigurator
                            columns={field.options}
                            onChange={(newCols) => {
                              setFormEditorState((prev) => {
                                const copy = [...prev.fields];
                                copy[idx] = { ...copy[idx], options: newCols };
                                return { ...prev, fields: copy };
                              });
                            }}
                            isDark={isDark}
                          />
                        )}

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(field.required)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFormEditorState((prev) => {
                                  const copy = [...prev.fields];
                                  copy[idx] = { ...copy[idx], required: checked };
                                  return { ...prev, fields: copy };
                                });
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Mandatory for Approver (Required)
                            </Typography>
                          }
                        />
                      </Stack>
                    </Card>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={() => setRightPanelTab(1)}
                    sx={{ borderRadius: 1, textTransform: "none", fontWeight: 600 }}
                  >
                    Preview Output
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={formSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={formSaving || !formEditorState.form_name.trim()}
                    onClick={handleSaveForm}
                    sx={{
                      borderRadius: 1,
                      bgcolor: "#4f46e5",
                      "&:hover": { bgcolor: "#4338ca" },
                      textTransform: "none",
                      fontWeight: 700,
                      px: 3,
                    }}
                  >
                    {formSaving ? "Saving..." : formEditorState.id ? "Update Form" : "Save New Form"}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              /* TAB 1: LIVE APPROVER OUTPUT VIEW */
              <Box>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: `1.5px solid ${isDark ? "#4338ca" : "#c7d2fe"}`,
                    backgroundColor: isDark ? "rgba(30, 27, 75, 0.35)" : "#f5f7ff",
                    boxShadow: "0 8px 24px rgba(79, 70, 229, 0.08)",
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        p: 0.8,
                        borderRadius: 1,
                        bgcolor: "#4f46e5",
                        color: "#fff",
                        display: "flex",
                      }}
                    >
                      <DynamicFormIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={800} color="#4338ca">
                          {formEditorState.form_name || "Untitled Form"}
                        </Typography>
                        <Chip
                          size="small"
                          label="Required Form"
                          color="primary"
                          sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                        />
                      </Stack>
                      {formEditorState.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {formEditorState.description}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1.5, borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(79, 70, 229, 0.15)" }} />

                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5, display: "block" }}>
                    Approver Input Fields (Live Test)
                  </Typography>

                  <Stack spacing={2}>
                    {formEditorState.fields.map((field, fIdx) => {
                      const fieldKey = field.id || fIdx;
                      const val = previewValues[fieldKey];
                      const isReq = field.required;

                      if (field.field_type === "boolean") {
                        return (
                          <Box
                            key={fieldKey}
                            sx={{
                              p: 1.25,
                              borderRadius: 1,
                              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(val)}
                                  onChange={(e) =>
                                    setPreviewValues((prev) => ({
                                      ...prev,
                                      [fieldKey]: e.target.checked,
                                    }))
                                  }
                                  color="primary"
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight={600}>
                                  {field.field_label || "Boolean Switch"} {isReq && <span style={{ color: "#ef4444" }}>*</span>}
                                </Typography>
                              }
                            />
                          </Box>
                        );
                      }

                      if (field.field_type === "select") {
                        const opts = field.options
                          ? field.options.split(",").map((s) => s.trim()).filter(Boolean)
                          : [];
                        return (
                          <FormControl key={fieldKey} size="small" fullWidth required={isReq}>
                            <InputLabel>{field.field_label || "Select Option"}</InputLabel>
                            <Select
                              value={val || ""}
                              label={field.field_label || "Select Option"}
                              onChange={(e) =>
                                setPreviewValues((prev) => ({
                                  ...prev,
                                  [fieldKey]: e.target.value,
                                }))
                              }
                              sx={{ bgcolor: isDark ? "rgba(30,41,59,0.6)" : "#fff", borderRadius: 1 }}
                            >
                              {opts.length === 0 ? (
                                <MenuItem disabled value="">
                                  <em>No options configured</em>
                                </MenuItem>
                              ) : (
                                opts.map((opt, oIdx) => (
                                  <MenuItem key={oIdx} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        );
                      }

                      if (field.field_type === "table") {
                        return (
                          <DynamicTableField
                            key={fieldKey}
                            label={field.field_label || "Description of Material / Service Required"}
                            columns={field.options}
                            value={val}
                            onChange={(newRows) =>
                              setPreviewValues((prev) => ({
                                ...prev,
                                [fieldKey]: newRows,
                              }))
                            }
                            required={isReq}
                          />
                        );
                      }

                      return (
                        <TextField
                          key={fieldKey}
                          size="small"
                          fullWidth
                          type={field.field_type === "number" ? "number" : field.field_type === "date" ? "date" : "text"}
                          multiline={field.field_type === "textarea"}
                          rows={field.field_type === "textarea" ? 3 : 1}
                          label={`${field.field_label || "Text Input"}${isReq ? " *" : ""}`}
                          value={val || ""}
                          onChange={(e) =>
                            setPreviewValues((prev) => ({
                              ...prev,
                              [fieldKey]: e.target.value,
                            }))
                          }
                          InputLabelProps={field.field_type === "date" ? { shrink: true } : undefined}
                          required={isReq}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: isDark ? "rgba(30,41,59,0.6)" : "#ffffff",
                              borderRadius: 1,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>

                  <Box sx={{ mt: 2.5, p: 1.25, borderRadius: 1, bgcolor: isDark ? "rgba(99, 102, 241, 0.1)" : "rgba(79, 70, 229, 0.06)", border: "1px dashed rgba(99, 102, 241, 0.3)" }}>
                    <Typography variant="caption" color="text.secondary">
                      🔒 <strong>Confidentiality Rule:</strong> Responses submitted by the approver through this form will only be visible to the immediate next approver and the Company Admin.
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setRightPanelTab(0)}
                    sx={{ borderRadius: 1, textTransform: "none", fontWeight: 700 }}
                  >
                    Edit Form Fields
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Confirmation Dialog for Clearing Workflow */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 1, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Clear Approval Workflow?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all saved approval steps for your organization. Requisitions will revert to direct single-action approval. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClear}
            color="error"
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1 }}
          >
            {saving ? "Clearing..." : "Clear Workflow"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Deleting Form */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 1, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Dynamic Form?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete form <strong>"{formEditorState.form_name}"</strong> (ID #{formEditorState.id})?
            Workflow steps referencing this form will no longer prompt approvers for it.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteForm}
            color="error"
            variant="contained"
            disabled={formDeleting}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1 }}
          >
            {formDeleting ? "Deleting..." : "Delete Form"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
